import { Component, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ApiPaginationResult,
  PaginationResult,
} from '../../../../shared/models/api-result.model';
import { ProductIdentifierType } from '../../../models/product-identifier-type.model';
import { ProductIdentifierTypeService } from '../../../services/product-identifier-type.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Table, TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { RouterLink } from '@angular/router';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { PaginationService } from '../../../../shared/services/pagination.service';

@Component({
  selector: 'app-product-identifier-type-list',
  imports: [
    FormsModule,
    InputTextModule,
    TableModule,
    ButtonModule,
    ToolbarModule,
    BreadcrumbModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
    ToastModule,
    RouterLink,
  ],
  providers: [ConfirmationService],
  templateUrl: './product-identifier-type-list.component.html',
})
export class ProductIdentifierTypeListComponent implements OnDestroy {
  @ViewChild('table') table?: Table;

  private productIdentifierTypeService = inject(ProductIdentifierTypeService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  paginationService = inject(PaginationService);

  productIdentifierTypes = signal<ProductIdentifierType[]>([]);

  pageNumber = signal<number>(1);
  pageSize = signal<number>(10);
  totalRecords = signal<number>(0);
  totalPages = signal<number>(0);

  loading = signal(false);

  nameFilter?: string;

  private destroyFilter$ = new Subject<void>();
  private filterChanged$ = new Subject<void>();

  constructor() {
    this.filterChanged$
      .pipe(debounceTime(500), takeUntil(this.destroyFilter$))
      .subscribe(() => {
        this.pageNumber.set(0);
        this.table?.reset();
      });
  }

  ngOnDestroy(): void {
    this.destroyFilter$.next();
    this.destroyFilter$.complete();
  }

  onFilterChange(): void {
    this.filterChanged$.next();
  }

  loadData(event: any): void {
    const page = this.paginationService.mapLazyEventToPageRequest(
      event,
      this.pageSize(),
    );
    this.pageNumber.set(page.pageNumber);
    this.pageSize.set(page.pageSize);

    this.loading.set(true);

    this.productIdentifierTypeService
      .getAll(this.pageNumber(), this.pageSize(), this.nameFilter)
      .subscribe({
        next: (response: ApiPaginationResult) => {
          this.loading.set(false);

          const page = response.data as PaginationResult;
          this.productIdentifierTypes.set(
            page.items as ProductIdentifierType[],
          );
          this.pageNumber.set(page.pageNumber);
          this.pageSize.set(page.pageSize);
          this.totalRecords.set(page.totalCount);
          this.totalPages.set(page.totalPages);
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao carregar os tipos de identificador',
              life: 3000,
            });
          }
        },
      });
  }

  requestUpdateStatus(item: ProductIdentifierType, event: MouseEvent): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: 'Deseja realmente alterar o status deste tipo?',
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Alterar',
      },
      accept: () => this.updateStatus(item),
    });
  }

  private updateStatus(item: ProductIdentifierType): void {
    this.loading.set(true);

    this.productIdentifierTypeService
      .updateStatus(item.id, !item.isActive)
      .subscribe({
        next: () => {
          this.loading.set(false);

          item.isActive = !item.isActive;

          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Status atualizado com sucesso',
            life: 2000,
          });
        },
        error: (e) => {
          this.loading.set(false);

          if (e instanceof HttpErrorResponse) {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao atualizar o status',
              life: 3000,
            });
          }
        },
      });
  }
}
